import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Crown, Shield, Check, Loader2, QrCode,
  ArrowLeft, Lock, AlertTriangle, AlertCircle, CheckCircle2, Info, Copy, Clock,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const formatCPF = (val) => {
  const nums = val.replace(/\D/g, '').slice(0, 11);
  if (nums.length <= 3) return nums;
  if (nums.length <= 6) return `${nums.slice(0, 3)}.${nums.slice(3)}`;
  if (nums.length <= 9) return `${nums.slice(0, 3)}.${nums.slice(3, 6)}.${nums.slice(6)}`;
  return `${nums.slice(0, 3)}.${nums.slice(3, 6)}.${nums.slice(6, 9)}-${nums.slice(9)}`;
};

const formatPhone = (val) => {
  const nums = val.replace(/\D/g, '').slice(0, 11);
  if (nums.length <= 2) return nums;
  if (nums.length <= 7) return `(${nums.slice(0, 2)}) ${nums.slice(2)}`;
  return `(${nums.slice(0, 2)}) ${nums.slice(2, 7)}-${nums.slice(7)}`;
};

// Validação real de CPF (algoritmo dos dígitos verificadores — Receita Federal).
const isValidCPF = (cpf) => {
  const nums = (cpf || '').replace(/\D/g, '');
  if (nums.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(nums)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(nums[i], 10) * (10 - i);
  let d1 = 11 - (sum % 11);
  if (d1 >= 10) d1 = 0;
  if (d1 !== parseInt(nums[9], 10)) return false;
  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(nums[i], 10) * (11 - i);
  let d2 = 11 - (sum % 11);
  if (d2 >= 10) d2 = 0;
  return d2 === parseInt(nums[10], 10);
};

const FALLBACK_PRICE = 14.90;

const Checkout = () => {
  const { planId } = useParams();
  const { user, token, refreshUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [nomeCompleto, setNomeCompleto] = useState('');
  const [cpf, setCpf] = useState('');
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');
  const [loading, setLoading] = useState(false);
  const [pix, setPix] = useState(null);           // { subscription_id, pix_qr_code_base64, pix_copia_cola, payment_link }
  const [paid, setPaid] = useState(false);
  const [error, setError] = useState('');
  const [price, setPrice] = useState(FALLBACK_PRICE);
  const [duracao, setDuracao] = useState('1 mês');
  const [planLabel, setPlanLabel] = useState('Plano Mensal');
  const [isTestEnv, setIsTestEnv] = useState(false);

  const pollTimer = useRef(null);

  // Fetch authoritative price + flags from backend (always wins over fallback)
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/plans/`, { timeout: 10000 })
      .then(resp => {
        if (cancelled) return;
        setIsTestEnv(!!resp.data?.is_test_environment);
        const wanted = (planId === 'anual') ? 'anual' : 'mensal';
        const list = resp.data?.plans || [];
        const match = list.find(p => p.id === wanted) || list.find(p => p.id === 'mensal');
        if (match) {
          if (typeof match.preco === 'number') setPrice(match.preco);
          if (match.duracao_label) setDuracao(match.duracao_label);
          if (match.nome) setPlanLabel(match.nome);
        }
      })
      .catch(() => { /* keep fallback */ });
    return () => { cancelled = true; };
  }, [planId]);

  useEffect(() => {
    // Wait for auth bootstrap to finish before deciding to redirect
    if (authLoading) return;
    if (!user) { navigate('/auth'); return; }
    setNomeCompleto(user.nome || '');
    setEmail(user.email || '');
    setTelefone(user.telefone || '');
    // Normalize URL: only 'mensal' and 'anual' are valid; everything else
    // (including legacy 'semestral', 'pro_mensal', 'trimestral') falls back to mensal.
    if (planId && !['mensal', 'anual'].includes(planId)) {
      navigate('/checkout/mensal', { replace: true });
    }
  }, [authLoading, user, planId, navigate]);

  // Poll /api/auth/me every 4s while a PIX is pending; flip to success once plano=='pro'
  const startPolling = useCallback(() => {
    if (pollTimer.current) return;
    pollTimer.current = setInterval(async () => {
      try {
        const resp = await axios.get(`${API}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
          timeout: 6000,
        });
        if (resp.data?.plano === 'pro') {
          clearInterval(pollTimer.current);
          pollTimer.current = null;
          setPaid(true);
          if (refreshUser) refreshUser();
          toast.success('Pagamento confirmado! Plano ativado.');
        }
      } catch { /* silent — keep trying */ }
    }, 4000);
  }, [token, refreshUser]);

  useEffect(() => {
    // Cleanup on unmount
    return () => { if (pollTimer.current) clearInterval(pollTimer.current); };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const rawCpf = cpf.replace(/\D/g, '');
    if (rawCpf.length !== 11) { setError('Informe um CPF válido (11 dígitos).'); return; }
    if (!isValidCPF(rawCpf)) { setError('CPF inválido. Verifique os dígitos e tente novamente.'); return; }
    if (!nomeCompleto.trim()) { setError('Informe seu nome completo.'); return; }
    if (!email.trim() || !email.includes('@')) { setError('Informe um e-mail válido.'); return; }

    setLoading(true);
    try {
      // All paid plans now go through Mercado Pago with the chosen plano_id.
      const planoId = planId === 'anual' ? 'anual' : 'mensal';
      const payload = {
        plano_id: planoId,
        cpf: rawCpf,
        nome_completo: nomeCompleto.trim(),
        email: email.trim(),
        telefone: telefone.replace(/\D/g, ''),
      };
      const resp = await axios.post(`${API}/api/payments/mp/subscribe`, payload, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
        timeout: 30000,
      });
      setPix(resp.data);
      startPolling();
      toast.success('QR Code gerado! Escaneie para pagar.');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[Checkout] subscribe failed:', {
        status: err?.response?.status, statusText: err?.response?.statusText,
        detail: err?.response?.data?.detail, message: err?.message, code: err?.code,
      });
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      if (status === 429) setError('Muitas tentativas. Aguarde alguns instantes.');
      else if (status === 401) setError('Sua sessão expirou. Faça login novamente.');
      else if (typeof detail === 'string') setError(detail);
      else if (err.code === 'ECONNABORTED') setError('O gateway demorou para responder. Tente novamente.');
      else if (err.message === 'Network Error') setError('Não foi possível conectar ao gateway. Verifique sua conexão.');
      else if (status >= 500) setError('Instabilidade no gateway. Tente novamente em instantes.');
      else setError('Não foi possível gerar a cobrança PIX. Tente novamente.');
      toast.error('Falha ao gerar PIX.');
    } finally { setLoading(false); }
  };

  const copyPix = async () => {
    if (!pix?.pix_copia_cola) return;
    try {
      await navigator.clipboard.writeText(pix.pix_copia_cola);
      toast.success('Código PIX copiado!');
    } catch { toast.error('Não foi possível copiar. Selecione manualmente.'); }
  };

  // Success state
  if (paid) {
    return (
      <div className="max-w-lg mx-auto py-8 px-4 space-y-6" data-testid="checkout-success">
        <Card className="p-6 sm:p-8 bg-white border border-[#006030] rounded-md text-center">
          <CheckCircle2 className="w-16 h-16 text-[#006030] mx-auto mb-4" />
          <h2 className="text-xl font-black text-[#0F1C2E] mb-2">Pagamento confirmado!</h2>
          <p className="text-sm text-[#475569] mb-4">
            Seu <strong>Plano Completo</strong> foi ativado. Você tem acesso completo por {duracao}.
          </p>
          <Button onClick={() => navigate('/')} className="bg-[#006030] hover:bg-[#004020] text-white">
            Ir para a página inicial
          </Button>
        </Card>
      </div>
    );
  }

  // PIX state — after subscription created
  if (pix?.pix_qr_code_base64 || pix?.payment_link) {
    return (
      <div className="max-w-lg mx-auto py-6 px-4 space-y-5" data-testid="checkout-pix">
        <Link to="/planos" className="inline-flex items-center gap-1 text-sm text-[#006030] hover:underline">
          <ArrowLeft className="w-4 h-4" /> Voltar aos Planos
        </Link>
        <Card className="p-5 sm:p-6 bg-white border-2 border-[#006030] rounded-md text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <QrCode className="w-5 h-5 text-[#006030]" />
            <h2 className="text-base font-black text-[#0F1C2E]">Pague com PIX</h2>
          </div>
          <p className="text-xs text-[#475569] mb-4">
            R$ {price.toFixed(2).replace('.', ',')} · Acesso por {duracao}
          </p>

          {pix.pix_qr_code_base64 ? (
            <div className="flex justify-center mb-4">
              <img
                src={`data:image/png;base64,${pix.pix_qr_code_base64}`}
                alt="QR Code PIX"
                className="w-56 h-56 border border-[#E2E8F0] rounded-md"
                data-testid="pix-qr-code"
              />
            </div>
          ) : (
            <div className="bg-[#F4F6F8] border border-[#E2E8F0] rounded-md p-4 mb-4 text-xs text-[#475569]">
              QR Code não disponível no momento. Clique em "Abrir página do pagamento" abaixo.
            </div>
          )}

          {pix.pix_copia_cola && (
            <>
              <p className="text-[10px] text-[#94A3B8] mb-1">PIX Copia e Cola:</p>
              <div className="flex items-center gap-2 mb-3">
                <Input
                  value={pix.pix_copia_cola}
                  readOnly
                  className="text-[10px] font-mono bg-[#F4F6F8]"
                  data-testid="pix-copy-paste"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={copyPix}
                  className="shrink-0"
                  data-testid="pix-copy-btn"
                >
                  <Copy className="w-3.5 h-3.5" />
                </Button>
              </div>
            </>
          )}

          {pix.payment_link && (
            <a
              href={pix.payment_link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-[#006030] hover:underline mb-4"
              data-testid="pix-invoice-link"
            >
              Abrir página oficial do pagamento
            </a>
          )}

          <div className="mt-4 p-3 bg-[#FFF8E1] border border-[#F0D000]/40 rounded-md text-left flex items-start gap-2">
            <Clock className="w-4 h-4 text-[#F57F17] shrink-0 mt-0.5 animate-pulse" />
            <div>
              <p className="text-xs font-bold text-[#0F1C2E]" data-testid="pix-status">Aguardando pagamento…</p>
              <p className="text-[10px] text-[#475569]">
                Seu plano será ativado automaticamente em alguns segundos após a confirmação do PIX.
                Você pode fechar esta página. Vamos liberar o acesso mesmo se estiver offline.
              </p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Form state
  return (
    <div className="max-w-2xl mx-auto py-6 px-4 space-y-5" data-testid="checkout-page">
      <Link to="/planos" className="inline-flex items-center gap-1 text-sm text-[#006030] hover:underline">
        <ArrowLeft className="w-4 h-4" /> Voltar aos Planos
      </Link>

      {/* Plan Summary */}
      <Card className="p-4 bg-white border-2 border-[#006030] rounded-md" data-testid="checkout-plan-summary">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#E8F5E9] rounded-full flex items-center justify-center">
              <Crown className="w-5 h-5 text-[#006030]" />
            </div>
            <div>
              <h2 className="text-lg font-black text-[#0F1C2E]">{planLabel}</h2>
              <Badge className="bg-[#006030] text-white text-[10px]">
                {planId === 'anual' ? 'Pagamento único por 12 meses' : 'Renova a cada 30 dias'}
              </Badge>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-black text-[#006030]" data-testid="checkout-price">
              R$ {price.toFixed(2).replace('.', ',')}
            </p>
            <p className="text-xs text-[#94A3B8]">
              {planId === 'anual' ? 'por ano' : 'por mês'}
            </p>
          </div>
        </div>
      </Card>

      {isTestEnv && (
        <Card
          className="p-3 bg-[#FFF8E1] border border-[#FFE082] rounded-md flex items-start gap-2"
          data-testid="checkout-test-banner"
        >
          <AlertCircle className="w-4 h-4 text-[#7C5800] shrink-0 mt-0.5" />
          <p className="text-[12px] text-[#7C5800] leading-snug">
            <strong>Ambiente de teste.</strong> Use as contas de teste do Mercado Pago. Quando as credenciais
            de produção forem inseridas, o PIX vai funcionar normalmente sem necessidade de alteração no app.
          </p>
        </Card>
      )}

      {/* Billing Info */}
      <Card className="p-4 sm:p-5 bg-white border border-[#E2E8F0] rounded-md" data-testid="checkout-billing-section">
        <h3 className="text-sm font-bold text-[#0F1C2E] mb-3 flex items-center gap-2">
          <Shield className="w-4 h-4 text-[#006030]" /> Dados para Cobrança
        </h3>
        {error && (
          <div className="flex items-center gap-2 p-2 bg-[#FFEBEE] border border-[#C62828]/20 rounded-md mb-3" data-testid="checkout-error">
            <AlertTriangle className="w-4 h-4 text-[#C62828] shrink-0" />
            <p className="text-xs text-[#C62828]">{error}</p>
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-wider">Nome Completo</Label>
              <Input value={nomeCompleto} onChange={e => setNomeCompleto(e.target.value)} placeholder="Maria da Silva" className="mt-1 border-[#E2E8F0]" required data-testid="checkout-nome" />
            </div>
            <div>
              <Label className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-wider">CPF</Label>
              <Input
                value={cpf}
                onChange={e => setCpf(formatCPF(e.target.value))}
                placeholder="000.000.000-00"
                className={`mt-1 ${cpf.replace(/\D/g,'').length === 11 && !isValidCPF(cpf) ? 'border-[#C62828]' : 'border-[#E2E8F0]'}`}
                required
                data-testid="checkout-cpf"
              />
              {cpf.replace(/\D/g,'').length === 11 && !isValidCPF(cpf) && (
                <p className="text-[10px] text-[#C62828] mt-1" data-testid="cpf-invalid-hint">
                  Este CPF não é válido. Verifique os dígitos.
                </p>
              )}
              {cpf.replace(/\D/g,'').length === 11 && isValidCPF(cpf) && (
                <p className="text-[10px] text-[#2E7D32] mt-1">CPF válido</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-wider">E-mail</Label>
              <Input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="seu@email.com" className="mt-1 border-[#E2E8F0]" required data-testid="checkout-email" />
            </div>
            <div>
              <Label className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-wider">Telefone</Label>
              <Input value={telefone} onChange={e => setTelefone(formatPhone(e.target.value))} placeholder="(11) 99999-9999" className="mt-1 border-[#E2E8F0]" data-testid="checkout-telefone" />
            </div>
          </div>

          <div className="p-3 bg-[#F4F6F8] rounded-md">
            <p className="text-[10px] text-[#94A3B8] leading-relaxed">
              Ao prosseguir, você concorda com os{' '}
              <Link to="/termos" className="text-[#006030] hover:underline" target="_blank">Termos de Uso</Link>,{' '}
              <Link to="/privacidade" className="text-[#006030] hover:underline" target="_blank">Política de Privacidade</Link>{' '}e{' '}
              <Link to="/cancelamento" className="text-[#006030] hover:underline" target="_blank">Política de Cancelamento</Link>.
              Pagamento instantâneo via PIX. Acesso ativado automaticamente após a confirmação.
            </p>
          </div>

          <Button type="submit" disabled={loading} className="w-full bg-[#006030] hover:bg-[#004020] text-white font-medium h-11 text-sm" data-testid="checkout-submit">
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Gerando cobrança PIX…</>
            ) : (
              <><QrCode className="w-4 h-4 mr-2" /> Pagar R$ {price.toFixed(2).replace('.', ',')} via PIX</>
            )}
          </Button>
        </form>
      </Card>

      <div className="flex items-center justify-center gap-4 text-[10px] text-[#94A3B8]">
        <span className="flex items-center gap-1"><Lock className="w-3 h-3" /> Pagamento seguro</span>
        <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> Apenas PIX</span>
        <span className="flex items-center gap-1"><Check className="w-3 h-3" /> Ativação automática</span>
      </div>

      <div className="max-w-xl mx-auto p-3 bg-[#F0F6FF] border border-[#BFDBFE] rounded-md flex items-start gap-2">
        <Info className="w-4 h-4 text-[#1E3A5F] shrink-0 mt-0.5" />
        <p className="text-[11px] text-[#1E3A5F]">
          Após o clique em "Pagar via PIX", um QR Code será gerado. Use o app do seu banco para escanear e pagar.
          Assim que o PIX for confirmado, liberamos seu acesso <strong>automaticamente</strong>.
        </p>
      </div>
    </div>
  );
};

export default Checkout;
